/** Console links for the evidence a diagnose run assembled.
 *
 *  Refs are self-describing — the backend names a model and an id, and
 *  `consoleRouteFor` owns the route table — so this stays a thin translation
 *  rather than a second id-space vocabulary.
 *
 *  It deliberately offers only hops whose id space is certain. A ref carries
 *  the record's PRIMARY KEY, while several grid namespaces filter on a
 *  vendor-supplied string of the same name (`/import-runs?q=booking=` is the
 *  booking-system id, not our pk; `request=` is the correlation id, not the
 *  API_Request pk). Getting that wrong produces a link that silently returns
 *  the wrong rows, which is worse than no link — so anything not verifiable
 *  from the ref alone is left out. Never hand-build a `?q=`: `gridLinks.ts`
 *  is the only place that knows a grid's namespaces. */

import type { DiagnoseContextRef } from '@/api/diagnoseContext';
import { act, type ValueAction } from '@/components/ValueActions/ValueActions';
import { consoleRouteFor, cond, deliveryLogsHref, type Tenant } from '@/utils/gridLinks';

const NOUNS: Record<string, string> = {
  group_tour_user: 'booking',
  order: 'order',
  user: 'user',
  import_run: 'import run',
  event_delivery_log: 'delivery log',
  api_request: 'API request',
  notification: 'notification',
  tour_operator: 'tour operator',
};

export function refLabel(ref: DiagnoseContextRef): string {
  return ref.label || ref.id;
}

/** The detail route, or null when the model has no console page — callers
 *  render plain text then; a fabricated route that 404s is worse. */
export function refRoute(ref: DiagnoseContextRef): string | null {
  return consoleRouteFor(ref.model, ref.id);
}

export function refActions(ref: DiagnoseContextRef, tenant?: Tenant): ValueAction[] {
  const actions: ValueAction[] = [];
  const route = refRoute(ref);
  if (route) actions.push(act.open(route, `Open ${NOUNS[ref.model] ?? 'record'}`));

  // `participant` is the console booking pk — the one cross-grid hop a ref
  // can make unambiguously (`booking` on the same grid is the vendor string).
  if (ref.model === 'group_tour_user') {
    actions.push(
      act.filter(
        deliveryLogsHref([cond('participant', ref.id, 'exact')], { tenant }),
        'Delivery logs for this booking',
      ),
    );
  }

  actions.push(act.copy(ref.id, 'Copy id'));
  return actions;
}
